<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import {
    dateOnlyFromAppointmentPlanningInputValue,
    readAppointmentPlanningInputEventValue,
  } from "../../_shared/_logic/input-events"
  import BatchApplyActionBox from "../../../batch-apply-action-box"
  import type {
    AppointmentPlanningKeptSettings,
    PlanningSettingsDisabledState,
    PlanningSettingsOption,
    PlanningSettingsPanelHandlers,
    AppointmentPlanningFlexMode,
  } from "../../../../_logic/appointments-contract"
  import {
    KEEP_PRECONFIGURATION_VALUE,
    isKeepPreconfigurationChoice,
    reconcileKeepPreconfigurationSelection,
    withKeepPreconfigurationOption,
  } from "../../../../_logic/preconfiguration-keep"
  import {
    planningStartPresetOptions,
    type PlanningStartPreset,
  } from "../../../../_logic/planning-start-preset"

  type Props = {
    startDate: string
    planningStartPreset: PlanningStartPreset
    planningTimezone: string
    selectedEmployees: string[]
    flexPlanningMode: AppointmentPlanningFlexMode
    keptSettings: AppointmentPlanningKeptSettings
    flexPlanningAvailable?: boolean
    employeeOptions: PlanningSettingsOption[]
    flexPlanningOptions: PlanningSettingsOption[]
    disabledState: PlanningSettingsDisabledState
    handlers: PlanningSettingsPanelHandlers
    applySelectedCount?: number
    applyTargetCount?: number
    applyPending?: boolean
    applyDisabled?: boolean
    onApply: () => void | Promise<void>
  }

  let {
    startDate = $bindable(""),
    planningStartPreset = $bindable(),
    planningTimezone,
    selectedEmployees = $bindable<string[]>([]),
    flexPlanningMode = $bindable<AppointmentPlanningFlexMode>("skip"),
    keptSettings = $bindable(),
    flexPlanningAvailable = false,
    employeeOptions = [],
    flexPlanningOptions = [],
    disabledState,
    handlers,
    applySelectedCount = 0,
    applyTargetCount = 0,
    applyPending = false,
    applyDisabled = false,
    onApply,
  }: Props = $props()

  const formDisabled = $derived(disabledState.stepLocked || disabledState.planning)
  const presetReferenceDate = new Date()
  const startPresetOptions = $derived(
    planningStartPresetOptions(presetReferenceDate, planningTimezone),
  )
  const employeeSelectionLocked = $derived(employeeOptions.length === 1)
  const startPresetSelectOptions = $derived(withKeepPreconfigurationOption(startPresetOptions))
  const rotatingEmployeeSelectOptions = $derived(
    employeeSelectionLocked ? employeeOptions : withKeepPreconfigurationOption(employeeOptions),
  )
  const flexPlanningSelectOptions = $derived(withKeepPreconfigurationOption(flexPlanningOptions))
  const keepSetting = (setting: keyof AppointmentPlanningKeptSettings, kept: boolean) => {
    if (keptSettings[setting] !== kept) keptSettings = { ...keptSettings, [setting]: kept }
  }
  const startDateTooltip =
    "KehrBlatt sucht Termine ab dem Montag nach der gewählten Vorlaufzeit. Am Montag zählt die laufende Kalenderwoche mit; ab Dienstag zählt erst die nächste Kalenderwoche."
  const rotatingEmployeesTooltip =
    "Diese Mitarbeitenden werden initial in der Planung berücksichtigt. Die Terminplanung verteilt neue Terminvorschläge gleichmäßig auf diese Auswahl."
  const flexPlanningTooltip =
    "Flex-Termine belegen keinen festen Kalenderslot, sondern liegen in einem Zeitraum. Bei „Jeden zweiten freihalten“ darf die Sammelplanung je Mitarbeiter den 1., 3., 5. usw. Flexslot verwenden; der 2., 4., 6. usw. bleibt über den gesamten Planungshorizont frei."

  const syncPlanningStartPreset = (event: Event) => {
    if (formDisabled) return

    const input = readAppointmentPlanningInputEventValue(event)

    if (!input.found) return

    if (isKeepPreconfigurationChoice(input.value)) {
      keepSetting("startDate", true)

      return
    }

    const selected = startPresetOptions.find((option) => option.value === input.value)

    if (!selected) return

    keepSetting("startDate", false)
    planningStartPreset = selected.value
    const presetStartDate = selected.startDate

    if (presetStartDate) {
      startDate = presetStartDate
    }

    handlers.syncStartDate({ startDate, preset: planningStartPreset })
  }

  const syncCustomStartDate = (event: Event) => {
    if (formDisabled) return

    const input = readAppointmentPlanningInputEventValue(event)

    if (!input.found) return

    const date = dateOnlyFromAppointmentPlanningInputValue(input.value)

    if (date === undefined) return

    startDate = date ?? ""
    planningStartPreset = "custom"
    handlers.syncStartDate({ startDate, preset: planningStartPreset })
  }
</script>

{#snippet rotatingEmployeesField()}
  {#key employeeOptions.map((option) => option.value).join("|")}
    <Input
      type="select"
      placeholder="Terminvorschläge verteilen auf"
      options={rotatingEmployeeSelectOptions}
      multiple={true}
      bind:value={
        () => (keptSettings.selectedEmployees ? [KEEP_PRECONFIGURATION_VALUE] : selectedEmployees),
        (next) => {
          const values = reconcileKeepPreconfigurationSelection(
            keptSettings.selectedEmployees ? [KEEP_PRECONFIGURATION_VALUE] : selectedEmployees,
            Array.isArray(next) ? next.map(String) : [],
          )
          const kept = values.includes(KEEP_PRECONFIGURATION_VALUE)

          keepSetting("selectedEmployees", kept)

          if (!kept) selectedEmployees = values
        }
      }
      tooltip={rotatingEmployeesTooltip}
      helperText={employeeSelectionLocked
        ? "Für diese Planung steht nur dieser Mitarbeiter zur Verfügung."
        : "Neue Termine werden gleichmäßig auf diese Auswahl verteilt."}
      brightBackground={true}
      disabled={formDisabled ||
        !disabledState.hasRotatingEmployeeOptions ||
        employeeSelectionLocked}
      onChange={handlers.syncSelectedEmployees}
    />
  {/key}
{/snippet}

<section class="form light" aria-label="Voreinstellungen">
  <BatchApplyActionBox
    label="Vorkonfiguration"
    selectedCount={applySelectedCount}
    targetCount={applyTargetCount}
    pending={applyPending}
    disabled={applyDisabled}
    {onApply}
  >
    <div class="content">
      <div class="row">
        <Input
          type="select"
          placeholder="Planungsvorlauf"
          options={startPresetSelectOptions}
          value={keptSettings.startDate ? KEEP_PRECONFIGURATION_VALUE : planningStartPreset}
          tooltip={startDateTooltip}
          helperText="So bleibt genug Zeit für digitale Ankündigung und Terminbrief."
          brightBackground={true}
          disabled={formDisabled}
          onChange={syncPlanningStartPreset}
        />
        {#if !keptSettings.startDate && planningStartPreset === "custom"}
          <Input
            type="date"
            placeholder="Startdatum"
            value={startDate}
            helperText="Wählen Sie den ersten Tag, ab dem Termine gesucht werden."
            brightBackground={true}
            disabled={formDisabled}
            onChange={syncCustomStartDate}
          />
        {/if}
        {@render rotatingEmployeesField()}
        {#if flexPlanningAvailable}
          <Input
            type="select"
            placeholder="Flex-Termine erlauben"
            options={flexPlanningSelectOptions}
            bind:value={
              () =>
                keptSettings.flexPlanningMode ? KEEP_PRECONFIGURATION_VALUE : flexPlanningMode,
              (next) => {
                const kept = isKeepPreconfigurationChoice(next)

                keepSetting("flexPlanningMode", kept)

                if (!kept) flexPlanningMode = next as AppointmentPlanningFlexMode
              }
            }
            tooltip={flexPlanningTooltip}
            helperText="Jeden zweiten freihalten reserviert je Mitarbeiter die Flexslots 2, 4, 6 usw."
            brightBackground={true}
            disabled={formDisabled}
            onChange={handlers.syncFlexPlanningMode}
          />
        {/if}
      </div>
    </div>
  </BatchApplyActionBox>
</section>

<style lang="less">
  .content {
    display: flex;
    flex-direction: column;
    gap: 1.8rem;
  }

  .content :global(.row > *) {
    min-width: 0;
  }
</style>
